import React from 'react';
import './PrivacyPage.css';

function PrivacyPage() {
  return (
    <div className="privacy-page">
      <h1>Privacy Policy for NextLink</h1>

      <p className="last-updated"><strong>Last Updated: September 16, 2025</strong></p>

      <h2>1. Introduction</h2>
      <p>NextLink ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application NextLink (the "App").</p>

      <h2>2. Information We Collect</h2>

      <h3>2.1 Personal Information You Provide</h3>
      <ul>
        <li><strong>Account Information</strong>: Name, email address, and authentication credentials</li>
        <li><strong>Profile Information</strong>: Profile photos, and job titles</li>
        <li><strong>Content</strong>: Messages, images, documents, and files you share through the app</li>
        <li><strong>Group Information</strong>: Group names, descriptions, and administrative settings</li>
        <li><strong>Event Data</strong>: Events, polls, quick forms, and responses</li>
        <li><strong>Payment Information</strong>: Payment details processed through Stripe (we do not store complete payment card information)</li>
      </ul>

      <h3>2.2 Information Collected Automatically</h3>
      <ul>
        <li><strong>Device Information</strong>: Device type, operating system version, and unique device identifiers</li>
        <li><strong>Usage Data</strong>: How you interact with the app, features used, and time spent</li>
        <li><strong>Push Notification Tokens</strong>: Device tokens to send notifications</li>
      </ul>

      <h3>2.3 Information from Third Parties</h3>
      <ul>
        <li><strong>Contacts</strong>: With your permission, we access your device contacts to help you find friends and invite them to groups</li>
        <li><strong>Firebase Services</strong>: Authentication, database, and cloud storage services provided by Google Firebase</li>
        <li><strong>Stripe</strong>: Payment processing services (Stripe handles payment data according to their privacy policy)</li>
      </ul>

      <h2>3. Permissions We Request</h2>

      <h3>3.1 iOS Permissions</h3>
      <ul>
        <li><strong>Contacts Access</strong>: To help you find friends and invite them to groups</li>
        <li><strong>Camera</strong>: To take photos for profile pictures and sharing</li>
        <li><strong>Photo Library</strong>: To select photos for sharing and profile pictures</li>
        <li><strong>Notifications</strong>: To send you important updates and messages</li>
      </ul>

      <h3>3.2 Android Permissions</h3>
      <ul>
        <li><strong>Contacts</strong>: To access your contact list for friend discovery and invitations</li>
        <li><strong>Camera</strong>: To capture photos and videos</li>
        <li><strong>Storage</strong>: To access and save photos, documents, and other files</li>
        <li><strong>Network Access</strong>: To connect to the internet and sync data</li>
        <li><strong>Notifications</strong>: To receive push notifications</li>
      </ul>

      <h2>4. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide and maintain the app's functionality</li>
        <li>Facilitate communication between group members</li>
        <li>Process payments for group events and activities</li>
        <li>Send notifications about group activities and updates</li>
        <li>Help you find and invite contacts to join groups</li>
        <li>Provide customer support and respond to inquiries</li>
        <li>Improve app performance and user experience</li>
        <li>Ensure security and prevent unauthorized access</li>
      </ul>

      <h2>5. Information Sharing and Disclosure</h2>

      <h3>5.1 With Other Users</h3>
      <ul>
        <li>Your profile information (name, photo) is visible to other members of your groups</li>
        <li>Messages and content you share are visible to group members</li>
        <li>Event participation and responses may be visible to group administrators</li>
      </ul>

      <h3>5.2 With Service Providers</h3>
      <ul>
        <li><strong>Google Firebase</strong>: For authentication, database, storage, and cloud functions</li>
        <li><strong>Stripe</strong>: For payment processing (subject to Stripe's privacy policy)</li>
        <li><strong>Expo Notifications</strong>: For push notification delivery</li>
      </ul>

      <h3>5.3 Legal Requirements</h3>
      <p>We may disclose your information if required by law, court order, or to protect our rights and safety.</p>

      <h2>6. Data Storage and Security</h2>
      <ul>
        <li><strong>Cloud Storage</strong>: Your data is stored securely using Google Firebase services</li>
        <li><strong>Encryption</strong>: Data is encrypted in transit and at rest</li>
        <li><strong>Authentication</strong>: Secure authentication through Firebase Auth</li>
        <li><strong>Access Controls</strong>: Strict access controls limit who can access your data</li>
      </ul>

      <h2>7. Your Rights and Choices</h2>

      <h3>7.1 Account Controls</h3>
      <ul>
        <li>Update your profile information at any time</li>
        <li>Delete content you've shared (subject to group policies)</li>
        <li>Leave groups you've joined</li>
      </ul>

      <h3>7.2 Contact Permissions</h3>
      <ul>
        <li>Grant or revoke contact access permissions in your device settings</li>
        <li>We only access contacts when you explicitly use contact-related features</li>
      </ul>

      <h3>7.3 Notifications</h3>
      <ul>
        <li>Control notification preferences in the app settings</li>
        <li>Disable push notifications through your device settings</li>
      </ul>

      <h3>7.4 Data Deletion</h3>
      <ul>
        <li><strong><a href="/delete-account" style={{color: '#007bff', textDecoration: 'underline'}}>Request Account Deletion</a></strong> - Use our account deletion form to permanently delete your account and all associated data</li>
        <li>Account deletion will be processed within 30 days of your request</li>
        <li>Some data may be retained for legal or security purposes as outlined in our retention policy</li>
        <li>You can also contact us directly at help@ngexpresstech.com for account deletion requests</li>
      </ul>

      <h2>8. Children's Privacy</h2>
      <p>NextLink is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.</p>

      <h2>9. International Data Transfers</h2>
      <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.</p>

      <h2>10. Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy in the app and updating the "Last Updated" date. Your continued use of the app after changes constitutes acceptance of the updated policy.</p>

      <h2>11. Contact Information</h2>
      <div className="contact-info">
        <p>If you have questions about this Privacy Policy or our data practices, please contact us at:</p>
        <p><strong>Email</strong>: help@ngexpresstech.com<br />
        <strong>App</strong>: Use the in-app support feature<br />
        <strong>Website</strong>: <a href="http://nextlinkchat.com">http://nextlinkchat.com</a></p>

        <div style={{background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem', marginTop: '1rem'}}>
          <p><strong>Account Deletion:</strong> To delete your account and all associated data, please use our <a href="/delete-account" style={{color: '#007bff', textDecoration: 'underline', fontWeight: 'bold'}}>Account Deletion Form</a></p>
        </div>
      </div>

      <h2>12. California Privacy Rights</h2>
      <p>If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA), including the right to:</p>
      <ul>
        <li>Know what personal information we collect about you</li>
        <li>Request deletion of your personal information</li>
        <li>Opt-out of the sale of your personal information (we do not sell personal information)</li>
      </ul>

      <h2>13. European Union Rights</h2>
      <p>If you are in the European Union, you may have rights under the General Data Protection Regulation (GDPR), including:</p>
      <ul>
        <li>Access to your personal data</li>
        <li>Correction of inaccurate data</li>
        <li>Deletion of your data</li>
        <li>Portability of your data</li>
        <li>Objection to processing</li>
      </ul>

      <hr />

      <p className="footer-note">This privacy policy is designed to be transparent about our data practices while ensuring compliance with applicable privacy laws. By using NextLink, you acknowledge that you have read and understood this Privacy Policy.</p>
    </div>
  );
}

export default PrivacyPage;